/**
 * SAMALogo - Shared logo component that fetches from CMS
 * Ensures consistent logo usage across all pages
 */
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default logo fallback
const DEFAULT_LOGO = "https://ehvwjbglvapazrvyzevw.supabase.co/storage/v1/object/public/cms-assets/logos/SAMA_Logo_Primary.png";

// Cache the logo URL to avoid repeated API calls
let cachedLogoUrl = null;
let cachePromise = null;

async function fetchLogoFromCMS() {
  if (cachedLogoUrl) return cachedLogoUrl;
  if (cachePromise) return cachePromise;
  
  cachePromise = (async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/cms/content/public?section=logos`);
      if (response.ok) {
        const data = await response.json();
        const logoItem = data.find(item => 
          item.section === 'logos' && item.metadata?.key === 'primary_logo'
        );
        if (logoItem?.media_url) {
          cachedLogoUrl = logoItem.media_url;
          return cachedLogoUrl;
        }
      }
    } catch (error) {
      console.warn('Failed to fetch logo from CMS:', error);
    }
    cachedLogoUrl = DEFAULT_LOGO;
    return cachedLogoUrl;
  })();
  
  return cachePromise;
}

// Hook to get logo URL
export function useLogoUrl() {
  const [logoUrl, setLogoUrl] = useState(cachedLogoUrl || DEFAULT_LOGO);
  
  useEffect(() => {
    fetchLogoFromCMS().then(url => setLogoUrl(url));
  }, []);
  
  return logoUrl;
}

// Logo component with various sizes
export default function SAMALogo({ 
  className = '', 
  height = 40,
  variant = 'default', // 'default', 'white', 'dark'
  onClick = null
}) {
  const logoUrl = useLogoUrl();
  
  const filterStyle = variant === 'white' 
    ? 'brightness-0 invert' 
    : variant === 'dark' 
      ? 'brightness-0' 
      : '';
  
  return (
    <img
      src={logoUrl}
      alt="SAMA"
      className={`object-contain ${filterStyle} ${className}`}
      style={{ height: `${height}px` }}
      onClick={onClick}
    />
  );
}

// Export the default logo URL for direct use
export { DEFAULT_LOGO };
